import { CategoryItem } from "@src/hooks/query/useAllCategory";
import { getProductList, ProductItem } from "@src/hooks/query/useAllProduct";
import { buildSushiShopImageUrl } from "@src/lib/render-image/hooks/useSushiShopImage";
import {
  ImageFit,
  ImageFormat,
  ImagePosition,
} from "@src/lib/render-image/interface/ImageData";
import {
  IListingCategoryItem,
  IListingCategoryProduct,
} from "@src/layout/CategoryLayout/CategoryLayout";
import {
  CATALOG_OCCURRENCE_SEPARATOR,
  CATALOG_PATH_SEPARATOR,
} from "@src/layout/CategoryLayout/catalogTree";
import type { CatalogItem } from "@src/backbone/model/StoreContextModel";

export type ListingCategorySource = CategoryItem;
export type ListingProductSource = ProductItem;

const sortProductsByPosition = (
  products: IListingCategoryProduct[],
  product_positions?: Record<string, number> | null
): IListingCategoryProduct[] => {
  if (!product_positions || Object.keys(product_positions).length === 0) {
    return products;
  }

  return [...products].sort((a, b) => {
    const id_a = a?.id_product?.toString() || "";
    const id_b = b?.id_product?.toString() || "";
    const position_a = product_positions[id_a];
    const position_b = product_positions[id_b];

    if (position_a !== undefined && position_b !== undefined) {
      return position_a - position_b;
    }
    if (position_a !== undefined) return -1;
    if (position_b !== undefined) return 1;
    return 0;
  });
};

/**
 * RUN-8282: visibilité produit au rendu statique (GraphQL, sans contexte boutique).
 * Aligné sur ProductModel.isActive() (`active_web`) et ProductModel.isActiveInStore()
 * (`if (!store && print_if_no_store === false) → invisible`).
 * Le rendu SSR se fait toujours sans boutique sélectionnée : les deux règles s'appliquent.
 */
export const isProductVisibleWithoutStoreGraphql = (
  product: Pick<ListingProductSource, "active_web" | "print_if_no_store">
) => product.active_web !== false && product.print_if_no_store !== false;

export const getVisibleProductListGraphql = (
  category: ListingCategorySource,
  products: ListingProductSource[]
): IListingCategoryProduct[] =>
  (getProductList(products, category) as IListingCategoryProduct[]).filter(
    isProductVisibleWithoutStoreGraphql
  );

export const isCategoryActiveGraphql = (
  category: ListingCategorySource,
  /** RUN-8282: liste déjà filtrée si disponible — sinon fallback sur la liste brute. */
  visibleProductList?: IListingCategoryProduct[]
) => {
  const hasProducts = visibleProductList
    ? visibleProductList.length > 0
    : !!(category.products?.length ?? 0);
  return (
    !!category.active &&
    !!category.active_menu &&
    !!category.show_if_no_store &&
    hasProducts
  );
};

export const toListingCategoryItem = (
  category: ListingCategorySource,
  products: ListingProductSource[]
): IListingCategoryItem => {
  const productList = sortProductsByPosition(
    getVisibleProductListGraphql(category, products),
    category.product_positions as Record<string, number> | null | undefined
  );

  return {
    ...category,
    href: category.uri || "",
    productList,
  };
};

export const buildGraphqlCategoryList = (
  categories: ListingCategorySource[],
  products: ListingProductSource[]
): IListingCategoryItem[] =>
  categories.map((category) => toListingCategoryItem(category, products));

export const buildMenuCategoriesGraphql = (
  categories: ListingCategorySource[],
  products: ListingProductSource[]
): IListingCategoryItem[] => {
  const withProducts = buildGraphqlCategoryList(categories, products);
  const _list: IListingCategoryItem[] = [];

  withProducts.forEach((i) => {
    if (
      !i.is_navigation_by_desire &&
      i.active_menu &&
      !i.id_parent &&
      i.active
    ) {
      const children = withProducts.filter(
        (j) =>
          j.id_parent === i.id_category &&
          isCategoryActiveGraphql(j as CategoryItem, j.productList)
      );

      if (children.length) {
        _list.push({
          ...i,
          children,
          href: children[0].uri || "",
        });
      } else if (isCategoryActiveGraphql(i as CategoryItem, i.productList)) {
        _list.push(i);
      }
    }
  });

  return _list;
};

/** Identité unique d'un nœud : path en mode catalog, id_category sinon. */
const listingNodeKey = (category: IListingCategoryItem): string =>
  String(category.path ?? category.id_category ?? "");

/**
 * Contenu = produits rendus ou bannières catalog.
 * RUN-8282 : `productList` et non `category.products`, qui n'est pas filtrée de la
 * visibilité produit — une catégorie entièrement filtrée resterait dans la liste aplatie.
 */
const hasListingContent = (category: IListingCategoryItem): boolean =>
  !!category.productList?.length || !!category.catalogItems?.length;

/**
 * `activeCategoryId` est un nodeKey (état interne) ou un id_category brut (routage) :
 * nodeKey d'abord, repli id_category — première occurrence si catégorie dupliquée.
 */
export const resolveShellTargetIndex = (
  list: IListingCategoryItem[],
  activeCategoryId: string
): number => {
  const normalized = String(activeCategoryId);
  const exact = list.findIndex(
    (category) => listingNodeKey(category) === normalized
  );
  if (exact >= 0) return exact;
  const byCategoryId = list.findIndex(
    (category) => String(category.id_category) === normalized
  );
  return byCategoryId >= 0 ? byCategoryId : 0;
};

/** DFS pre-order sur N niveaux : le catalog est récursif, sans profondeur garantie. */
export const flattenCategories = (
  categories: IListingCategoryItem[],
  categoryId: string
) => {
  const list: IListingCategoryItem[] = [];
  const normalizedCategoryId = String(categoryId);
  let activeCategory: IListingCategoryItem | null = null;
  let activeByCategoryId: IListingCategoryItem | null = null;

  const walk = (nodes: IListingCategoryItem[]) => {
    nodes.forEach((node) => {
      if (hasListingContent(node)) {
        list.push(node);
        if (!activeCategory && listingNodeKey(node) === normalizedCategoryId) {
          activeCategory = node;
        }
        if (
          !activeByCategoryId &&
          String(node.id_category) === normalizedCategoryId
        ) {
          activeByCategoryId = node;
        }
      }
      if (node.children?.length) walk(node.children);
    });
  };
  walk(categories);

  if (!activeCategory) {
    activeCategory = activeByCategoryId || list[0] || null;
  }

  return { list, activeCategory };
};

/** Identifiants adressables du menu sur N niveaux : nodeKey et id_category. */
export const collectMenuCategoryIds = (
  menuCategories: IListingCategoryItem[]
): Set<string> => {
  const ids = new Set<string>();
  const walk = (nodes: IListingCategoryItem[]) => {
    nodes.forEach((category) => {
      if (category.id_category) ids.add(String(category.id_category));
      const key = listingNodeKey(category);
      if (key) ids.add(key);
      if (category.children?.length) walk(category.children);
    });
  };
  walk(menuCategories);
  return ids;
};

export const resolveActiveCategoryId = (
  pageContext: {
    id_category?: string;
    mainPage?: boolean;
  },
  menuCategories: IListingCategoryItem[]
): string => {
  const id = pageContext.id_category;
  const menuIds = collectMenuCategoryIds(menuCategories);

  if (id && id !== "all_categories" && menuIds.has(String(id))) {
    return String(id);
  }

  // Le 1er élément doit être celui de la liste APLATIE (celle qui
  // sert au calcul de shellTargetIndex) — pas "premier enfant du 1er groupe" :
  // si le 1er groupe a ses propres produits ET des enfants, le parent occupe
  // l'index 0 et l'enfant l'index 1, ce qui rendait isDeepLink vrai à tort en
  // arrivant sans catégorie précisée (masquait ListingPushes).
  const { list } = flattenCategories(menuCategories, "");
  const firstFlatId = list[0]?.id_category;
  if (firstFlatId) return String(firstFlatId);

  return String(menuCategories[0]?.id_category || "");
};

/** Breakpoint listing layout (aligné sur CategoryLayout / CategoryDetails.scss). */
export const LISTING_LAYOUT_BREAKPOINT = 992;

export const isListingDesktopViewport = (
  viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0
) => viewportWidth > LISTING_LAYOUT_BREAKPOINT;

export const CATEGORY_ANCHOR_SCROLL_OFFSET_MOBILE = 120;
export const CATEGORY_ANCHOR_SCROLL_OFFSET_DESKTOP = 64;

export const isCategoryDeepLink = (targetIndex: number) => targetIndex > 0;

export type CategoryLoadPhase = "initial" | "staticReady" | "active" | "full";

export type CategoryRenderKind = "none" | "shell" | "static" | "details";

export interface CategoryRenderSpec {
  kind: CategoryRenderKind;
  active: boolean;
  fillViewportBelowHeader: boolean;
}

/**
 * Détermine le type de rendu par catégorie selon la phase de chargement progressif.
 * - initial     : catégorie active Static seule (deep-link) — pas de shellsAbove visibles (LCP/first paint)
 * - staticReady : setupReady atteint — rendu identique à initial (pas encore d'interactivité)
 * - active      : catégorie active CategoryDetails (interactivité) — toujours sans shellsAbove
 * - full        : listing complet en CategoryDetails (catégories au-dessus révélées post-LCP)
 *
 * HYDRATION-FIX: ne plus peindre les shellsAbove avant la catégorie URL (flash Albert Adrià / Sushi Boxes).
 */
export const resolveCategoryRenderSpec = (
  index: number,
  shellTargetIndex: number,
  activeCategoryId: string,
  categoryId: string,
  phase: CategoryLoadPhase,
  /** Index max révélé par chunking idle en phase "full" (inclus). */
  fullChunkMaxIndex?: number,
  /** Indices explicitement débloqués en phase "full" (ordre par distance). */
  fullRevealedIndexes?: ReadonlySet<number>,
  /** ProductCard / prix sur la catégorie active (click) — sans ça static en phase full. */
  interactivityReady?: boolean
): CategoryRenderSpec | null => {
  const isDeepLink = isCategoryDeepLink(shellTargetIndex);
  const id = String(categoryId);
  const isActiveCategory = id === String(activeCategoryId);
  const isTargetIndex = index === shellTargetIndex;
  const fillViewportBelowHeader =
    isDeepLink && isTargetIndex && phase !== "full";

  if (phase === "initial" || phase === "staticReady") {
    // Deep-link : uniquement la catégorie URL (pas de shells au-dessus → first paint correct).
    if (isDeepLink && index < shellTargetIndex) {
      return null;
    }
    if (isTargetIndex) {
      return {
        kind: "static",
        active: true,
        fillViewportBelowHeader,
      };
    }
    return null;
  }

  if (phase === "active") {
    if (isDeepLink && index < shellTargetIndex) {
      return null;
    }
    if (isTargetIndex) {
      return {
        kind: "details",
        active: true,
        // CLS-FIX: garder le min-height deep-link jusqu'à phase "full"
        // (sinon Static→Details retire calc(100dvh) → shift ~0.7).
        fillViewportBelowHeader,
      };
    }
    return null;
  }

  // full — révélation progressive par distance (Set) pour ne pas réafficher
  // toutes les catégories au-dessus d'un coup (flash deep-link).
  const isChunkUnlocked = fullRevealedIndexes
    ? fullRevealedIndexes.has(index) || isActiveCategory
    : isActiveCategory ||
      index === shellTargetIndex ||
      (fullChunkMaxIndex !== undefined && index <= fullChunkMaxIndex);

  if (!isChunkUnlocked) {
    return null;
  }

  // Scroll → full avant click : autres catégories en details lazy, active reste static.
  if (isActiveCategory && !interactivityReady) {
    return {
      kind: "static",
      active: true,
      fillViewportBelowHeader: false,
    };
  }

  return {
    kind: "details",
    active: isActiveCategory,
    fillViewportBelowHeader: false,
  };
};

/** DOM static prêt (deep-link anchor, observers layout) — avant interactivité ProductCard. */
export const isContentReadyPhase = (phase: CategoryLoadPhase) =>
  phase === "staticReady" || phase === "active" || phase === "full";

/** Interactivité Backbone / quantifier disponible. */
export const isInteractivityReadyPhase = (phase: CategoryLoadPhase) =>
  phase === "active" || phase === "full";

/** @deprecated Préférer isContentReadyPhase ou isInteractivityReadyPhase. */
export const isActiveCategoryLoadPhase = isContentReadyPhase;

export const getCategoryAnchorScrollOffset = (
  viewportWidth: number = typeof window !== "undefined" ? window.innerWidth : 0
) => {
  // Offset dynamique = bas du chrome sticky (#header-bottom-container),
  // plus fiable que 120px fixe (header + delivery + tabs ≈ 190px mobile).
  if (typeof document !== "undefined") {
    const headerBottom = document.getElementById("header-bottom-container");
    if (headerBottom) {
      const bottom = headerBottom.getBoundingClientRect().bottom;
      if (bottom > 0) {
        return Math.round(bottom);
      }
    }
  }
  return viewportWidth <= LISTING_LAYOUT_BREAKPOINT
    ? CATEGORY_ANCHOR_SCROLL_OFFSET_MOBILE
    : CATEGORY_ANCHOR_SCROLL_OFFSET_DESKTOP;
};

export const DEEP_LINK_ANCHOR_TOLERANCE_PX = 40;

export interface CategoryAnchorScrollResult {
  applied: number;
  ideal: number;
  max: number;
  clamped: boolean;
}

export const scrollToCategoryAnchorElement = (
  target: HTMLElement,
  viewportWidth: number = typeof window !== "undefined" ? window.innerWidth : 0,
  behavior: ScrollBehavior = "auto"
): CategoryAnchorScrollResult => {
  const absoluteTop =
    target.getBoundingClientRect().top +
    (window.pageYOffset || document.documentElement.scrollTop || 0);
  const offset = getCategoryAnchorScrollOffset(viewportWidth);
  const ideal = Math.max(0, absoluteTop - offset);
  const max =
    typeof document !== "undefined"
      ? Math.max(
          0,
          document.documentElement.scrollHeight -
            (window.innerHeight || document.documentElement.clientHeight || 0)
        )
      : ideal;
  const applied = Math.min(ideal, max);
  window.scrollTo({ top: applied, behavior });
  return { applied, ideal, max, clamped: ideal > max };
};

/**
 * Élément d'ancre d'une catégorie.
 *
 * En mode catalog, les sections sont ancrées sur leur `path` (`parent>enfant`), alors que
 * les identifiants issus du routage (contexte de page, deep-link) restent des `id_category`
 * bruts — le back ne fournit pas de path dans l'URL. On retombe donc sur la première
 * section dont le path se termine par cet id_category. Cette première occurrence est
 * arbitraire lorsqu'une catégorie est dupliquée dans le catalog : l'URL ne permet pas de
 * distinguer les emplacements.
 */
const findCategoryAnchorElement = (id: string): HTMLElement | null => {
  if (typeof document === "undefined" || !id) return null;
  const direct = document.getElementById(`category-${id}`);
  if (direct) return direct;
  return (
    document.querySelector<HTMLElement>(
      `.category-details[data-id$="${CATALOG_PATH_SEPARATOR}${id}"]`
    ) ||
    // Duplication entre frères (RUN-8315) : path suffixé `~1`, si la 1re occurrence est élaguée.
    document.querySelector<HTMLElement>(
      `.category-details[data-id*="${CATALOG_PATH_SEPARATOR}${id}${CATALOG_OCCURRENCE_SEPARATOR}"], .category-details[data-id^="${id}${CATALOG_OCCURRENCE_SEPARATOR}"]`
    )
  );
};

export const scrollToCategoryAnchorById = (
  id: string,
  behavior: ScrollBehavior = "auto"
): boolean => {
  if (typeof document === "undefined") return false;
  const target = findCategoryAnchorElement(id);
  if (!target) return false;
  scrollToCategoryAnchorElement(target, undefined, behavior);
  return true;
};

export const getCategoryAnchorMisalignment = (
  categoryId: string,
  viewportWidth: number = typeof window !== "undefined" ? window.innerWidth : 0
): number | null => {
  if (typeof document === "undefined") return null;
  const target = findCategoryAnchorElement(categoryId);
  if (!target) return null;
  const offset = getCategoryAnchorScrollOffset(viewportWidth);
  return Math.abs(target.getBoundingClientRect().top - offset);
};

export const isDeepLinkAnchorMisaligned = (
  categoryId: string,
  tolerancePx: number = DEEP_LINK_ANCHOR_TOLERANCE_PX
): boolean => {
  const delta = getCategoryAnchorMisalignment(categoryId);
  if (delta === null) return true;
  return delta > tolerancePx;
};

export const buildCategoryDeepLinkScrollScript = (categoryDomId: string) =>
  `(function(){var id=${JSON.stringify(
    categoryDomId
  )};function o(){return window.innerWidth<=${LISTING_LAYOUT_BREAKPOINT}?${CATEGORY_ANCHOR_SCROLL_OFFSET_MOBILE}:${CATEGORY_ANCHOR_SCROLL_OFFSET_DESKTOP};}function s(){var e=document.getElementById(id);if(!e)return;var doc=document.documentElement;var st=window.pageYOffset||doc.scrollTop||0;var top=e.getBoundingClientRect().top+st;var ideal=Math.max(0,top-o());var max=Math.max(0,doc.scrollHeight-(window.innerHeight||doc.clientHeight||0));var applied=Math.min(ideal,max);window.scrollTo(0,applied);}s();if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",s);})();`;

/**
 * Positionne le strip mobile SSR en `position:fixed` aligné sur
 * #header-bottom-container SANS déplacer le nœud DOM.
 * Pas de scrollTo : scrollY=0 reste stable (évite le jump quand le header se compacte).
 * Purge aussi @@scroll Gatsby (sessionStorage) — sinon ScrollHandler.componentDidMount
 * restaure un Y périmé (ex. 140) ~2s après un first paint correct.
 */
export const buildCategorySsrMobileMenuRelocateScript = () =>
  `(function(){try{var path=location.pathname;var prefix="@@scroll|"+path;for(var i=sessionStorage.length-1;i>=0;i--){var k=sessionStorage.key(i);if(k&&k.indexOf(prefix)===0)sessionStorage.removeItem(k);}}catch(e){}var m=document.querySelector(".listing-category__menu--ssr-mobile");var t=document.getElementById("header-bottom-container");if(!m||!t)return;var wrap=m.closest(".listing-category__menu");if(wrap)wrap.setAttribute("data-ssr-menu-relocated","true");function syncMenu(){m.style.top=Math.round(t.getBoundingClientRect().top)+"px";}syncMenu();if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",syncMenu);requestAnimationFrame(syncMenu);window.addEventListener("resize",syncMenu,{passive:true});var header=t.closest("header")||document.querySelector("#header,header,.default-header");if(typeof ResizeObserver!=="undefined"){try{var ro=new ResizeObserver(syncMenu);ro.observe(t);if(header)ro.observe(header);}catch(e){}}var ticks=0;var iv=setInterval(function(){syncMenu();ticks+=1;if(ticks>=20)clearInterval(iv);},200);})();`;

export const buildInitialCategoryBlocks = (
  menuCategories: IListingCategoryItem[],
  activeCategoryId: string
) => {
  const { list, activeCategory } = flattenCategories(
    menuCategories,
    activeCategoryId
  );
  // nodeKey et non id_category : sur un catalog avec catégories dupliquées, repasser par
  // l'id_category ferait retomber l'index sur la première occurrence.
  const resolvedActiveId = activeCategory
    ? listingNodeKey(activeCategory)
    : String(activeCategoryId);
  const targetIndex = resolveShellTargetIndex(list, resolvedActiveId);
  const shellsAbove = targetIndex > 0 ? list.slice(0, targetIndex) : [];
  const active = list[targetIndex] || activeCategory || list[0] || null;

  return {
    list,
    targetIndex,
    shellsAbove,
    activeCategory: active,
    activeCategoryId: resolvedActiveId,
  };
};

export const findParentMenuCategory = (
  menuCategories: IListingCategoryItem[],
  category?: IListingCategoryItem | null
) => {
  if (!category?.id_parent) return undefined;
  return menuCategories.find((c) => c.id_category === category.id_parent);
};

export const buildCategoryBackgroundImageUrls = (category: {
  background_mobile?: string | null;
  background_desktop?: string | null;
}) => {
  const mobileBackgroundImageId =
    category.background_mobile || category.background_desktop;
  const desktopBackgroundImageId =
    category.background_desktop || category.background_mobile;

  const mobileBackgroundImageUrl = mobileBackgroundImageId
    ? buildSushiShopImageUrl({
        id: mobileBackgroundImageId,
        fit: ImageFit.COVER,
        position: ImagePosition.TOP,
        width: 720,
        height: 1280,
        format: ImageFormat.WEBP,
      })
    : undefined;

  const desktopBackgroundImageUrl = desktopBackgroundImageId
    ? buildSushiShopImageUrl({
        id: desktopBackgroundImageId,
        fit: ImageFit.COVER,
        position: ImagePosition.TOP,
        width: 1440,
        height: 1080,
        format: ImageFormat.WEBP,
      })
    : undefined;

  return {
    mobileBackgroundImageUrl,
    desktopBackgroundImageUrl,
    backgroundImageUrl: mobileBackgroundImageUrl || desktopBackgroundImageUrl,
  };
};

export type CategoryGridFormat =
  | "standard_regular"
  | "standard_hybrid"
  | "standard_large"
  | "chef_regular"
  | "chef_large"
  | "chef_hybrid";

export interface CategoryGridBannerSlot {
  id_banner: string;
  position: number;
  nbr_column_desktop: number;
}

/** Banner GraphQL visible en cold load (aucun magasin sélectionné). */
export type ColdLoadBannerSource = {
  id_banner?: string | null;
  active?: boolean | null;
  all_store?: boolean | null;
  show_if_no_store?: boolean | null;
  nbr_column_desktop?: number | null;
  nbr_row_mobile?: number | null;
  picture_landscape?: string | null;
  picture_portrait?: string | null;
  from?: string | null;
  to?: string | null;
  desktop?: boolean | null;
  webmobile?: boolean | null;
  customer_group?: ReadonlyArray<string | null> | null;
  visibility_type?: string | null;
  visibilities?: ReadonlyArray<{
    active?: boolean | null;
    day?: number | null;
    from?: string | null;
    to?: string | null;
  } | null> | null;
};

/**
 * Aligné BannerModel.isActiveResponsive(false|true|undefined).
 * `nbr_column_desktop === 0` / `nbr_row_mobile === 0` = inactif sur le viewport
 * (ex. kenko #937 : webmobile true mais nbr_row_mobile 0 → desktop only).
 */
export const isSsrBannerActiveDesktop = (
  banner: Pick<
    ColdLoadBannerSource,
    "desktop" | "nbr_column_desktop"
  > | null | undefined
): boolean =>
  !!banner?.desktop && (banner.nbr_column_desktop ?? 0) !== 0;

export const isSsrBannerActiveMobile = (
  banner: Pick<
    ColdLoadBannerSource,
    "webmobile" | "nbr_row_mobile"
  > | null | undefined
): boolean => !!banner?.webmobile && (banner.nbr_row_mobile ?? 0) !== 0;

export const isSsrBannerActiveOnViewport = (
  banner: ColdLoadBannerSource | null | undefined,
  isMobile: boolean
): boolean =>
  isMobile
    ? isSsrBannerActiveMobile(banner)
    : isSsrBannerActiveDesktop(banner);

export const isSsrBannerActiveAny = (
  banner: ColdLoadBannerSource | null | undefined
): boolean =>
  isSsrBannerActiveDesktop(banner) || isSsrBannerActiveMobile(banner);

/**
 * Hypothèse Lighthouse / nouveau visiteur : pas de store.
 * Aligné ListingPushSsr (`store_restriction === "1"` ‖ `show_if_no_store`).
 */
export const isColdLoadVisibleBanner = (
  banner: ColdLoadBannerSource | null | undefined
): boolean => {
  if (!banner?.id_banner) return false;
  if (banner.active === false) return false;
  return !!(banner.all_store || banner.show_if_no_store);
};

/** Groupes guest — aligné CustomerModel.getCustomerGroup() non connecté. */
const SSR_GUEST_CUSTOMER_GROUPS = ["1", "2"] as const; // NOT_CONNECTED, ALL

/**
 * Fenêtre [from, to] — aligné BannerModel.isActiveFromTo().
 */
export const isBannerActiveFromTo = (
  banner: Pick<ColdLoadBannerSource, "from" | "to"> | null | undefined,
  now: Date = new Date()
): boolean => {
  const fromRaw = banner?.from;
  const toRaw = banner?.to;
  const from = fromRaw ? String(fromRaw).replace(/-/g, "/") : "";
  const to = toRaw ? String(toRaw).replace(/-/g, "/") : "";
  if (from) {
    const fromDate = new Date(from);
    if (!Number.isNaN(fromDate.getTime()) && now.getTime() < fromDate.getTime()) {
      return false;
    }
  }
  if (to) {
    const toDate = new Date(to);
    if (!Number.isNaN(toDate.getTime()) && now.getTime() > toDate.getTime()) {
      return false;
    }
  }
  return true;
};

const parseHmsToSeconds = (hms: string): number | null => {
  const parts = hms.split(":").map((p) => Number(p));
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return null;
  const [h, m, s = 0] = parts;
  return h * 3600 + m * 60 + s;
};

/**
 * Créneau horaire du jour — aligné BannerModel.isMomentVisible() sur un tableau
 * indexé comme moment : index = (day() + 6) % 7 (lundi → 0).
 */
export const isBannerMomentVisible = (
  banner: Pick<ColdLoadBannerSource, "visibility_type" | "visibilities"> | null | undefined,
  now: Date = new Date()
): boolean => {
  if (banner?.visibility_type === "never") return false;
  if (banner?.visibility_type === "always") return true;

  const slots = (banner?.visibilities || []).filter(Boolean) as Array<{
    active?: boolean | null;
    from?: string | null;
    to?: string | null;
  }>;
  if (!slots.length) {
    // BannerModel : visibility absente / invalide → false (+ warn).
    return false;
  }

  const momentDay = now.getDay(); // Dimanche = 0
  const index = (momentDay + 7 - 1) % 7;
  const slot = slots[index];
  if (!slot) return false;
  if (slot.active === false) return false;
  if (!slot.from || !slot.to) return false;

  const fromSec = parseHmsToSeconds(String(slot.from));
  const toSec = parseHmsToSeconds(String(slot.to));
  if (fromSec == null || toSec == null) return false;

  const nowSec =
    now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  return nowSec >= fromSec && nowSec <= toSec;
};

export const isBannerVisibleForGuestCustomer = (
  banner: Pick<ColdLoadBannerSource, "customer_group"> | null | undefined
): boolean => {
  const groups = banner?.customer_group;
  if (!groups || !groups.length) return true;
  return groups.some(
    (g) => g != null && (SSR_GUEST_CUSTOMER_GROUPS as readonly string[]).includes(String(g))
  );
};

/**
 * Paint SSG / pre-setup : même verdict que BannerModel sans magasin (id_store=0),
 * guest, sans filtre tags — pour éviter flash SSR→hydratation.
 */
export const isSsrVisibleBanner = (
  banner: ColdLoadBannerSource | null | undefined,
  now: Date = new Date()
): boolean => {
  if (!isColdLoadVisibleBanner(banner)) return false;
  if (!isBannerActiveFromTo(banner, now)) return false;
  // Aligné isActiveResponsive(undefined) : desktop ‖ mobile (flags + nbr_*).
  if (!isSsrBannerActiveAny(banner)) return false;
  if (!isBannerVisibleForGuestCustomer(banner)) return false;
  if (!isBannerMomentVisible(banner, now)) return false;
  return true;
};

export interface ProductBannerLayout {
  imageWidthDesktop: number;
  imageHeightDesktop?: number;
  imageAspectRatioDesktop: number;
  imageWidthMobile: number;
  imageAspectRatioMobile: number;
  pushBottomSpace: boolean;
  outputPixelDensitiesDesktop: number[];
  colNumber: number;
  isLarge: boolean;
  isLargeOrHybrid: boolean;
  isChef: boolean;
}

/** Dimensions / aspect ProductBanner — partagé SSR ↔ hydraté. */
export const resolveProductBannerLayout = (options: {
  format?: string | null;
  nbr_column_desktop?: number | null;
  index?: number;
}): ProductBannerLayout => {
  const format = options.format || "standard_regular";
  const nbr_column_desktop = options.nbr_column_desktop ?? 1;
  const index = options.index ?? 0;
  const isLarge = /(_large)$/.test(format);
  const isLargeOrHybrid = /(_large|_hybrid)$/.test(format);
  const isChef = /^chef_/.test(format);
  const colNumber = isLargeOrHybrid ? 3 : 4;
  let imageWidthDesktop: number;
  let imageHeightDesktop: number | undefined;
  let imageAspectRatioDesktop: number;
  const imageWidthMobile = 420;
  const imageAspectRatioMobile = 1;
  let pushBottomSpace = true;
  let outputPixelDensitiesDesktop = [1, 1.5];

  if (isChef && index < 2) {
    imageWidthDesktop = 745;
    imageAspectRatioDesktop = 1;
    pushBottomSpace = false;
  } else if (nbr_column_desktop === 2) {
    if (isLargeOrHybrid) {
      imageWidthDesktop = 996;
      imageHeightDesktop = 593;
      imageAspectRatioDesktop = imageWidthDesktop / imageHeightDesktop;
    } else {
      imageWidthDesktop = 745;
      imageHeightDesktop = 470;
      imageAspectRatioDesktop = imageWidthDesktop / imageHeightDesktop;
    }
  } else if (nbr_column_desktop === 3) {
    imageWidthDesktop = 1498;
    imageAspectRatioDesktop = colNumber;
    pushBottomSpace = false;
  } else {
    imageWidthDesktop = isLargeOrHybrid ? 494 : 369;
    imageHeightDesktop = isLargeOrHybrid ? 598 : 473;
    imageAspectRatioDesktop = imageWidthDesktop / imageHeightDesktop;
    outputPixelDensitiesDesktop = isLargeOrHybrid
      ? outputPixelDensitiesDesktop
      : [1.5, 2];
  }

  return {
    imageWidthDesktop,
    imageHeightDesktop,
    imageAspectRatioDesktop,
    imageWidthMobile,
    imageAspectRatioMobile,
    pushBottomSpace,
    outputPixelDensitiesDesktop,
    colNumber,
    isLarge,
    isLargeOrHybrid,
    isChef,
  };
};

/** URLs WebP bit-exact ProductBanner / CategoryBannerSsr (mobile portrait, desktop landscape). */
export const buildProductBannerImageUrls = (options: {
  picture_landscape?: string | null;
  picture_portrait?: string | null;
  format?: string | null;
  nbr_column_desktop?: number | null;
  index?: number;
}) => {
  const layout = resolveProductBannerLayout(options);
  const landscapeId =
    options.picture_landscape || options.picture_portrait || null;
  const portraitId =
    options.picture_portrait || options.picture_landscape || null;

  const desktopHref = landscapeId
    ? buildSushiShopImageUrl({
        id: landscapeId,
        width: layout.imageWidthDesktop,
        height: layout.imageHeightDesktop
          ? layout.imageHeightDesktop
          : Math.round(
              layout.imageWidthDesktop / layout.imageAspectRatioDesktop
            ),
        format: ImageFormat.WEBP,
      })
    : null;

  const mobileHref = portraitId
    ? buildSushiShopImageUrl({
        id: portraitId,
        width: layout.imageWidthMobile,
        height: Math.round(
          layout.imageWidthMobile / layout.imageAspectRatioMobile
        ),
        format: ImageFormat.WEBP,
      })
    : null;

  return { layout, desktopHref, mobileHref };
};

export interface CategoryGridProductBlock {
  type: "product";
  product: IListingCategoryProduct;
}

export interface CategoryGridBannerBlock {
  type: "banner";
  id_banner: string;
  position: number;
  nbr_column_desktop: number;
}

export type CategoryGridBlock =
  | CategoryGridProductBlock
  | CategoryGridBannerBlock;

const parseArbitraryLines = (arbitrary_lines?: string | null) =>
  (arbitrary_lines || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

export const getCategoryGridColumns = (format: CategoryGridFormat) => {
  const isLarge = /_large$/.test(format || "");
  const isHybrid = /_hybrid$/.test(format || "");
  return isLarge || isHybrid ? 3 : 4;
};

export const buildCategoryBannerSlotsFromGraphql = (
  category: Pick<IListingCategoryItem, "banners">,
  allBanner?: ReadonlyArray<ColdLoadBannerSource>,
  options?: { coldLoadOnly?: boolean }
): CategoryGridBannerSlot[] => {
  const coldLoadOnly = options?.coldLoadOnly ?? false;
  const result: CategoryGridBannerSlot[] = [];
  category.banners?.forEach((banner) => {
    if (!banner?.id_banner) return;
    const bannerData = allBanner?.find(
      (b) => b.id_banner === banner.id_banner?.toString()
    );
    if (!bannerData) return;
    if (coldLoadOnly && !isSsrVisibleBanner(bannerData)) return;
    result.push({
      id_banner: String(banner.id_banner),
      position: banner.position || 0,
      nbr_column_desktop: bannerData.nbr_column_desktop || 3,
    });
  });
  return result;
};

export const buildProductsAndBanners = (
  productList: IListingCategoryProduct[],
  bannerSlots: CategoryGridBannerSlot[],
  /** Ordre imposé par le back (catalog dynamique) ; sinon insertion par `position`. */
  catalogItems?: CatalogItem[]
): CategoryGridBlock[] => {
  if (catalogItems?.length) {
    const productById = new Map(
      productList
        .filter((product) => !!product)
        .map((product) => [String(product.id_product), product])
    );
    const slotById = new Map(
      bannerSlots.map((slot) => [String(slot.id_banner), slot])
    );
    const ordered: CategoryGridBlock[] = [];
    catalogItems.forEach((item) => {
      if (item.type === "product") {
        const product = productById.get(String(item.id_product));
        if (product) ordered.push({ type: "product", product });
        return;
      }
      const slot = slotById.get(String(item.id_banner));
      if (slot) ordered.push({ type: "banner", ...slot });
    });
    return ordered;
  }

  const blocks: CategoryGridBlock[] = productList
    .filter((product) => !!product)
    .map((product) => ({
      type: "product" as const,
      product,
    }));

  bannerSlots.forEach((banner) => {
    blocks.splice(banner.position || 0, 0, {
      type: "banner",
      id_banner: banner.id_banner,
      position: banner.position,
      nbr_column_desktop: banner.nbr_column_desktop,
    });
  });

  return blocks;
};

export const getCategoryGridItemSpan = (options: {
  index: number;
  format: CategoryGridFormat;
  bannerColumn?: number;
  arbitrary_lines?: string | null;
  isMobile?: boolean;
  bannerCount?: number;
  productCount?: number;
}) => {
  const {
    index,
    format,
    bannerColumn = 0,
    arbitrary_lines,
    isMobile = false,
    bannerCount = 0,
    productCount = 0,
  } = options;
  const columns = getCategoryGridColumns(format);
  const baseSpan = 12 / columns;
  const isChef = /^chef_/.test(format || "");
  const isLarge = /_large$/.test(format || "");
  const isHybrid = /_hybrid$/.test(format || "");
  const arbitrary_lines_array = parseArbitraryLines(arbitrary_lines);

  if (isMobile) {
    return bannerCount + productCount;
  }

  let span = baseSpan;
  if (arbitrary_lines_array.length) {
    let i = 0;
    span = 0;
    for (const line of arbitrary_lines_array) {
      if (index >= i && index < i + parseInt(line, 10)) {
        span = 12 / parseInt(line, 10);
        break;
      }
      i += parseInt(line, 10);
    }
    if (!span) {
      span =
        12 /
        parseInt(arbitrary_lines_array[arbitrary_lines_array.length - 1], 10);
    }
  } else if (index < 2 && isChef) {
    span = 6;
  } else if (bannerColumn >= 3) {
    span = 12;
  } else if (bannerColumn >= 2) {
    span = isLarge || isHybrid ? 8 : 6;
  }
  return span;
};

export const getProductListingPictureId = (
  product: Pick<IListingCategoryProduct, "pictures">
): string => {
  const pictures = product.pictures ?? [];
  const rollingStart = pictures.find((picture) =>
    picture?.types?.includes("rollingstart")
  );
  return rollingStart?.id_object_picture || "";
};

/** @deprecated Préférer getProductListingPictureId (type rollingstart, aligné ProductCard). */
export const getProductCoverPictureId = getProductListingPictureId;

/** URLs illustration produit alignées sur ProductBlock / ProductCard (rollingstart, cover 614/284). */
export const buildProductIllustrationUrl = (
  product: Pick<IListingCategoryProduct, "pictures">,
  viewportWidth: number = typeof window !== "undefined"
    ? window.innerWidth
    : LISTING_LAYOUT_BREAKPOINT + 1
) => {
  const id = getProductListingPictureId(product);
  if (!id) return undefined;
  const isMobile = viewportWidth <= LISTING_LAYOUT_BREAKPOINT;
  const size = isMobile ? 284 : 614;
  return buildSushiShopImageUrl({
    id,
    width: size,
    height: size,
    fit: ImageFit.COVER,
    position: ImagePosition.CENTER,
    format: ImageFormat.WEBP,
  });
};

export const resolveActiveCategoryFirstProductPreloadHref = (
  pageContext: { id_category?: string },
  categories: ListingCategorySource[],
  products: ListingProductSource[],
  viewportWidth: number = LISTING_LAYOUT_BREAKPOINT
): string | null => {
  const menuCategories = buildMenuCategoriesGraphql(categories, products);
  const activeId = resolveActiveCategoryId(pageContext, menuCategories);
  const { activeCategory } = buildInitialCategoryBlocks(
    menuCategories,
    activeId
  );
  const firstProduct = activeCategory?.productList?.[0];
  if (!firstProduct) return null;

  // Bit-exact avec CategoryDetailsStatic / ProductBlock eager (284 mobile, 614 desktop).
  return buildProductIllustrationUrl(firstProduct, viewportWidth) || null;
};

/**
 * LCP-FIX: 1re banner cold-load en tête de grille (position 0) de la catégorie active.
 * Mobile = portrait ; desktop = landscape — bit-exact CategoryBannerSsr.
 */
export const resolveActiveCategoryFirstBannerPreload = (
  pageContext: { id_category?: string },
  categories: ListingCategorySource[],
  products: ListingProductSource[],
  allBanner: ReadonlyArray<ColdLoadBannerSource>,
  viewportWidth: number = LISTING_LAYOUT_BREAKPOINT
): {
  mobileHref: string | null;
  desktopHref: string | null;
} | null => {
  const menuCategories = buildMenuCategoriesGraphql(categories, products);
  const activeId = resolveActiveCategoryId(pageContext, menuCategories);
  const { activeCategory } = buildInitialCategoryBlocks(
    menuCategories,
    activeId
  );
  if (!activeCategory) return null;

  const slots = buildCategoryBannerSlotsFromGraphql(activeCategory, allBanner, {
    coldLoadOnly: true,
  });
  // Première banner avec image active sur au moins un viewport (pas seulement pos 0
  // desktop-only — sinon preload mobile de #937 kenko alors que nbr_row_mobile=0).
  const bannerDataById = new Map(
    (allBanner || [])
      .filter((b) => b?.id_banner)
      .map((b) => [String(b.id_banner), b] as const)
  );

  let preloadBanner: ColdLoadBannerSource | null = null;
  let preloadSlot: CategoryGridBannerSlot | null = null;
  for (const slot of slots) {
    const data = bannerDataById.get(String(slot.id_banner));
    if (!data) continue;
    if (!data.picture_landscape && !data.picture_portrait) continue;
    if (!isSsrBannerActiveAny(data)) continue;
    preloadBanner = data;
    preloadSlot = slot;
    break;
  }
  if (!preloadBanner || !preloadSlot) return null;

  const { desktopHref, mobileHref } = buildProductBannerImageUrls({
    picture_landscape: preloadBanner.picture_landscape,
    picture_portrait: preloadBanner.picture_portrait,
    format: activeCategory.format,
    nbr_column_desktop: preloadSlot.nbr_column_desktop,
    index: 0,
  });

  void viewportWidth;
  const activeMobile = isSsrBannerActiveMobile(preloadBanner);
  const activeDesktop = isSsrBannerActiveDesktop(preloadBanner);
  const resolvedMobile = mobileHref || desktopHref || null;
  const resolvedDesktop = desktopHref || mobileHref || null;

  if (!activeMobile && !activeDesktop) return null;

  return {
    // Pas de preload mobile si banner desktop-only (évite LCP fantôme + bande passante).
    mobileHref: activeMobile ? resolvedMobile : null,
    desktopHref: activeDesktop ? resolvedDesktop : null,
  };
};
